import React, { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  DataGridView,
  Button,
  Row,
  Col,
  NMFormPopup,
  NMMenu,
  NMPopup,
} from "@neumetric/blueprint-lib/components";
import { auditActions, getAuditList, getAuditStatusList } from "../../store";
import {
  getCurrentUser,
  isAuditee,
  isGoverner,
} from "@neumetric/blueprint-lib/store";
import { TrackerColumns, EmptyTrackerComponent, DetailsFields } from "./Helper";

const LEGACY_AUDITOR = process.env.REACT_APP_LEGACY_AUDITOR_PATH;

export const AuditTrackerScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const _currentPage = useRef(null);
  const currentUser = useSelector(getCurrentUser);
  const auditList = useSelector(getAuditList);
  const statusList = getAuditStatusList();
  const [openDetails, setOpenDetails] = React.useState(null);
  const [state, setState] = React.useState({
    showDialog: false,
  });

  React.useEffect(() => {
    fetchAuditList(1);
    dispatch(auditActions.setCurrentAudit(null));
    dispatch(auditActions.setCurrentControl(null));
  }, []);

  const updateState = (iNewState = {}) => {
    setState((prev) => ({ ...prev, ...iNewState }));
  };

  const fetchAuditList = (pageNo) => {
    _currentPage.current = pageNo;
    dispatch(auditActions.fetchAuditlist({ page: pageNo, per_page: 10 }));
  };

  const getTrackerColumns = useCallback(() => {
    const columns = TrackerColumns.map((_) => ({ ..._ }));
    if (isAuditee(currentUser)) {
      columns[3].editableFlag = false;
      columns[3].dataIndex = "status_obj";
    }
    return columns;
  }, [currentUser]);

  const handleOpenDetails = (row) => {
    setOpenDetails(row);
  };

  const handleClose = () => {
    setOpenDetails(null);
    updateState({
      showDialog: false,
      updateRecord: null,
      updateStatus: null,
    });
  };

  const handleMenuClick = useCallback(
    (item) => {
      if (item.id === "switch_v1" && LEGACY_AUDITOR) {
        const sessionToken = currentUser.sessionToken;
        window.location.href = `${LEGACY_AUDITOR}/login?sessionToken=${sessionToken}`;
      }
    },
    [currentUser],
  );

  const getOptionsForDetails = (attribute) => {
    if (attribute === "status") {
      return statusList;
    }
    return [];
  };

  const handlePageChange = (pageNo) => {
    fetchAuditList(pageNo);
  };

  const updateAuditStatus = (audit, status) => {
    updateState({
      isSubmmitting: true,
    });
    const action = auditActions.updateAuditStatus({
      auditId: audit.id,
      status,
      orgId: audit.ent_org.id,
    });
    action.onComplete = () => {
      updateState({
        showDialog: false,
        updateRecord: null,
        updateStatus: null,
        isSubmmitting: false,
      });
      fetchAuditList(_currentPage.current);
    };
    dispatch(action);
  };

  const handleStatusChange = (cellInfos, newStatus) => {
    const { column, record } = cellInfos;
    if (column.dataIndex === "status") {
      const showProp = ["complete", "revision", "submitted"].includes(
        newStatus.toLowerCase(),
      );
      if (showProp) {
        updateState({
          showDialog: true,
          updateRecord: record,
          updateStatus: newStatus,
        });
      } else {
        updateAuditStatus(record, newStatus);
      }
    }
  };
  const canEditAudit = (audit) => {
    if (isAuditee(currentUser)) {
      return false;
    }
    const status = (audit.status || "").toLowerCase();
    return ["pending", "submitted", "revision"].includes(status);
  };

  const rows = Array.isArray(auditList) ? auditList : auditList?.rows || [];

  const isEmpty = rows.length === 0;

  return (
    <Col className="w-100 h-100 pos-relative overflow-hide">
      <Row className="w-100 mb-20 pr-10" justify="end">
        {!isEmpty && !isAuditee(currentUser) && (
          <Button
            icon="ADD"
            label="New Audit"
            classes={{}}
            onClick={() => navigate("/A/T/new")}
          />
        )}
        {LEGACY_AUDITOR && (
          <NMMenu
            placement="bottomRight"
            showArrow={true}
            items={[
              {
                label: "View in Auditor v1",
                id: "switch_v1",
                variant: "ACTION",
                shouldCloseOnClick: true,
                classes: { item: "fw-400" },
              },
            ]}
            onMenuClick={handleMenuClick}
            headerButton={{
              btnType: "OUTLINED",
              icon: "DOT_MENU",
              classes: { button: "ml-16", icon: "fs-3 mt-10" },
            }}
          />
        )}
      </Row>
      <Row className="w-100 h-100 mb-10 pl-10 pr-10 pt-10">
        <DataGridView
          columns={getTrackerColumns()}
          dataSource={auditList ? auditList.rows : null}
          rowKey="key"
          classes={{ root: "audit-tracker-table" }}
          getCellSemantics={(column, record) => {
            const semantics = {};
            if (column.dataIndex === "actions") {
              semantics.onLinkClick = () => handleOpenDetails(record);
            }
            if (column.dataIndex === "audit_id") {
              semantics.link = `${record.guid}`;
            }
            if (column.dataIndex === "status") {
              semantics.possibleValues = getAuditStatusList(record);
            }
            return semantics;
          }}
          pagination={
            auditList && auditList.meta
              ? {
                  totalCount: auditList.meta.total_entries,
                  pageSize: 10,
                  defaultPage: 1,
                  onPageChange: handlePageChange,
                }
              : null
          }
          EmptyComponent={<EmptyTrackerComponent />}
          getCellTypeRepresentation={(column) => {
            if (column.dataIndex === "status") {
              return "DOT";
            }
            return column.typeRepresentation;
          }}
          getCellFilterValue={(column, record) => {
            if (column.dataIndex === "status") {
              return record.status_obj;
            }
            return record[column.dataIndex];
          }}
          getCellInputProps={(cellInfos) => {
            const { column, record } = cellInfos;
            const props = { disabled: false };
            if (column.dataIndex === "status") {
              props.disabled =
                isGoverner(currentUser) &&
                (record.status || "").toLowerCase() === "complete";
            }
            return props;
          }}
          setCellValue={handleStatusChange}
        />
      </Row>
      {openDetails !== null && (
        <NMFormPopup
          rowKey="id"
          visible={openDetails}
          width="814px"
          title={
            <Row align="center" justify="between" className="w-100">
              <span className="fs-17 text-heading fw-600">Audit Details</span>
              {canEditAudit(openDetails) && (
                <Button
                  icon="EDIT"
                  label="Edit"
                  variant="ACTION"
                  size="small"
                  classes={{ button: "mr-10" }}
                  onClick={() => {
                    navigate(`/A/T/new/${openDetails?.id}`);
                  }}
                />
              )}
            </Row>
          }
          classes={{
            root: "audit-details-form",
            header:
              "bg-header pt-8 pb-8 audit-details-header nm-popup-header-shadow",
            content: "pt-12",
          }}
          showClose="label"
          onModalClose={handleClose}
          getOptions={getOptionsForDetails}
          fields={DetailsFields}
          formData={openDetails || {}}
        />
      )}
      {state.showDialog && (
        <NMPopup
          variant="INFO"
          visible={state.showDialog}
          title="Update Status"
          onClose={handleClose}
          width="420px"
          actions={[
            {
              label: "Cancel",
              icon: "CANCEL",
              btnType: "OUTLINED",
              disabled: state.isSubmmitting,
              onClick: handleClose,
            },
            {
              label: "Confirm",
              icon: "CHECK_CONFIRM",
              btnType: "CONTAINED",
              loading: state.isSubmmitting,
              onClick: () => {
                updateAuditStatus(state.updateRecord, state.updateStatus);
              },
            },
          ]}
        >
          <span>
            Are you sure you want to change the status to
            <span className="fw-600 pl-4">{state.updateStatus}</span>?
          </span>
        </NMPopup>
      )}
    </Col>
  );
};
